import type { ReactNode } from 'react'
import { MarbleMark } from './MarbleMark'
import s from './GridBar.module.css'

/**
 * The GRID BAR — ONE row above a collection's column headers (#2375, the
 * approved unified grid):
 *
 *   mark · commands | selection ·············· search · [Save as…] · view ▾ · ⋮
 *
 * The earlier two-band bar (a scope row, then a controls row of Filter /
 * Columns / Sort / Save view / Display / More) is gone: column operations live
 * in the column header's ⋮, and the view's own settings in the ⋮ at the end of
 * this row. The page header owns a root collection's title, and at depth > 0
 * the view selector carries the relationship name — so there is no title slot.
 *
 * ── The mark leads, and it is not decoration ────────────────────────────────
 * Christopher, 2026-08-06: "Before any title or any view selector, I want to
 * see that command K marble icon." With `onCommand` it opens the command
 * palette ALREADY SCOPED to this grid; without it (a host with no palette) it
 * is the same mark as chrome, `aria-hidden` — never a button that does
 * nothing (#964). Its glyph centre lands on the lead column's first slot, so
 * the mark, the header select-all and every row checkbox share one line.
 *
 * ── The leading slot is commands OR selection, never both ──────────────────
 * With one or more rows selected the host passes `selection` (the inline
 * `GridSelection` group) and the governed command buttons give way to it in
 * place. The bar's height does not change between the two states.
 *
 * ── Slots, not logic ────────────────────────────────────────────────────────
 * The bar owns the SHAPE — what sits where, in what order, and how it wraps
 * below 720px of its own width — and nothing about views, filters or columns.
 */
export interface GridBarProps {
  /** Opens the palette scoped to THIS grid. Omit only where the host has none. */
  onCommand?: () => void
  /** Accessible name for the mark button: "Command palette for Products". */
  commandLabel?: string
  /** Governed command buttons beside the mark (primary first, then secondary). */
  commands?: ReactNode
  /** The inline selection group; when present it replaces `commands`. */
  selection?: ReactNode
  /** The collection search (`GridSearch`). */
  search?: ReactNode
  /** "Save as…" — the host passes it only while the view is modified. */
  saveAs?: ReactNode
  /** The saved-view selector. */
  views?: ReactNode
  /** The view-settings ⋮ (`ViewSettingsMenu`). */
  settings?: ReactNode
  /** A nested collection: the smaller mark and the nested density. */
  dense?: boolean
  testId?: string
}

export function GridBar({
  onCommand, commandLabel, commands, selection, search, saveAs, views, settings, dense, testId,
}: GridBarProps) {
  const leading = selection ?? commands
  return (
    <div className={`${s.bar}${dense ? ` ${s.dense}` : ''}`} data-testid={testId ?? 'grid-bar'}
      data-gridbar-band="scope">
      {onCommand ? (
        <button
          type="button"
          className={s.mark}
          onClick={onCommand}
          aria-label={commandLabel}
          aria-haspopup="dialog"
          data-testid="grid-command"
        >
          <MarbleMark size={dense ? 15 : 18} />
        </button>
      ) : (
        /* The mark, as CHROME. `aria-hidden` because it is decoration here: a
           graphic with no action does not belong in the accessibility tree,
           and leaving it named would announce a control that does nothing. */
        <span className={s.mark} data-mark aria-hidden="true" data-testid="grid-mark">
          <MarbleMark size={dense ? 15 : 18} />
        </span>
      )}
      {leading != null && (
        <div className={s.left} data-gridbar-slot="commands" data-testid={selection != null ? undefined : 'grid-commands'}>
          {leading}
        </div>
      )}
      <div className={s.spacer} />
      {(search != null || saveAs != null || views != null || settings != null) && (
        <div className={s.right} data-gridbar-slot="trailing">
          {search}
          {saveAs}
          {/* The view and its ⋮ are ONE unit: they wrap together, never the ⋮
              alone on a line of its own (measured in a nested band at 1024). */}
          {(views != null || settings != null) && (
            <span className={s.viewPair} data-gridbar-view-pair="">
              {views != null && <span className={s.views} data-testid="grid-view">{views}</span>}
              {settings}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
